import { IndexedDBRepository, ObjectStoreDefinition } from 'helpers/IndexedDBRepository';

export type BarcodeSyncStatus = 'pending' | 'done' | 'failed';

export interface BarcodeEntity {
  id: number | undefined;
  barcode: string;
  status: BarcodeSyncStatus;
  accountID: string;
  employeeID: string;
  inventoryCountID: string;
  createdAt: string;
  updatedAt: string;
  itemID: string | undefined;
  description: string | undefined;
}

export interface BulkAddItemsResponse {
  bulkAddItemResults: ResponseItem[];
}
export interface ResponseItem {
  searchID: string;
  status: 'success' | 'failed';
  itemID?: number | undefined;
  description?: string | undefined;
}
interface MappedResult {
  barcodeEntity: BarcodeEntity;
  responseItem: ResponseItem | undefined;
}

export class BarcodeService {
  public readonly databaseName: string = 'BarcodeRepository';
  public readonly databaseVersion: number = 1;
  public readonly objectStoreName: string = 'BarcodeCollection';
  public readonly accountID: string;
  public readonly employeeID: string;
  public readonly inventoryCountID: string;
  protected readonly indexedDBRepository: IndexedDBRepository<BarcodeEntity>;

  constructor(accountID: string, employeeID: string, inventoryCountID: string) {
    this.accountID = accountID;
    this.employeeID = employeeID;
    this.inventoryCountID = inventoryCountID;
    this.indexedDBRepository = this.createRepository();
  }

  protected createRepository(): IndexedDBRepository<BarcodeEntity> {
    const objectStoreDefinition: ObjectStoreDefinition = {
      name: this.objectStoreName,
      options: {},
      indexes: [
        {
          name: 'compoundIDs',
          keyPath: ['accountID', 'employeeID', 'inventoryCountID'],
          options: { unique: false },
        },
        {
          name: 'compoundIDsStatus',
          keyPath: ['accountID', 'employeeID', 'inventoryCountID', 'status'],
          options: { unique: false },
        },
      ],
    };
    return new IndexedDBRepository<BarcodeEntity>(
      this.databaseName,
      this.databaseVersion,
      objectStoreDefinition
    );
  }

  protected createEntityFromBarcode(barcode: string): Omit<BarcodeEntity, 'id'> {
    const now = new Date().toISOString();
    return {
      barcode,
      status: 'pending',
      accountID: this.accountID,
      employeeID: this.employeeID,
      inventoryCountID: this.inventoryCountID,
      createdAt: now,
      updatedAt: now,
      itemID: undefined,
      description: undefined,
    };
  }

  protected isBarcodeEntityAccessAllowed(barcodeEntity: BarcodeEntity): boolean {
    return (
      barcodeEntity.accountID === this.accountID &&
      barcodeEntity.employeeID === this.employeeID &&
      barcodeEntity.inventoryCountID === this.inventoryCountID
    );
  }

  public closeConnection(): void {
    this.indexedDBRepository.closeDatabase();
  }

  public async addBarcode(barcode: string): Promise<BarcodeEntity> {
    const key = await this.indexedDBRepository.add(this.createEntityFromBarcode(barcode));
    return this.indexedDBRepository.get(key);
  }

  public async updateBarcodes(barcodeEntities: BarcodeEntity[]): Promise<void> {
    const filteredBarcodeEntities = barcodeEntities.filter(
      (barcodeEntity) =>
        barcodeEntity.id !== undefined && this.isBarcodeEntityAccessAllowed(barcodeEntity)
    );
    await this.indexedDBRepository.updateBulk(filteredBarcodeEntities);
  }

  public async getBarcodes(status?: BarcodeSyncStatus): Promise<BarcodeEntity[]> {
    if (status) {
      return this.indexedDBRepository.getByIndex('compoundIDsStatus', [
        this.accountID,
        this.employeeID,
        this.inventoryCountID,
        status,
      ]);
    }
    return this.indexedDBRepository.getByIndex('compoundIDs', [
      this.accountID,
      this.employeeID,
      this.inventoryCountID,
    ]);
  }

  public async deleteBarcodes(status?: BarcodeSyncStatus): Promise<IDBValidKey> {
    return this.indexedDBRepository.deleteWhere((barcodeEntity) => {
      if (!this.isBarcodeEntityAccessAllowed(barcodeEntity)) return false;
      if (status) return barcodeEntity.status === status;
      return true;
    });
  }

  protected mapBarcodeEntitiesToResponseItems(
    barcodeEntities: BarcodeEntity[],
    responseItems: ResponseItem[]
  ): MappedResult[] {
    return barcodeEntities.map((barcodeEntity) => {
      const index = responseItems.findIndex(
        (responseItem) => responseItem.searchID === barcodeEntity.barcode
      );
      if (index !== -1) {
        const responseItem = responseItems[index];
        responseItems.splice(index, 1);
        return { barcodeEntity, responseItem };
      }
      return { barcodeEntity, responseItem: undefined };
    });
  }

  protected responseItemStatusToBarcodeSyncStatus(
    status: ResponseItem['status']
  ): BarcodeSyncStatus {
    return status === 'success' ? 'done' : 'failed';
  }

  public async syncBarcodes(): Promise<void> {
    const barcodeEntities = await this.getBarcodes('pending');
    if (barcodeEntities.length === 0) {
      return;
    }
    try {
      const response = await fetch(
        `/admin/inventory/inventory-count/${this.inventoryCountID}/bulk-add-items`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            itemIds: barcodeEntities.map((barcodeEntity: BarcodeEntity) => barcodeEntity.barcode),
          }),
        }
      );
      if (!response.ok) throw new Error();
      const result: BulkAddItemsResponse = await response.json();
      const mappedResults = this.mapBarcodeEntitiesToResponseItems(
        barcodeEntities,
        result.bulkAddItemResults
      );
      await this.updateBarcodes(
        mappedResults.map(({ barcodeEntity, responseItem }: MappedResult) => {
          const updatedBarcodeEntity = { ...barcodeEntity, updatedAt: new Date().toISOString() };
          if (responseItem) {
            updatedBarcodeEntity.status = this.responseItemStatusToBarcodeSyncStatus(
              responseItem.status
            );
            if (responseItem.status === 'success') {
              updatedBarcodeEntity.itemID = String(responseItem.itemID);
              updatedBarcodeEntity.description = responseItem.description;
            }
          } else {
            updatedBarcodeEntity.status = 'failed';
          }
          return updatedBarcodeEntity;
        })
      );
    } catch (error) {
      await this.updateBarcodes(
        barcodeEntities.map((barcodeEntity) => ({
          ...barcodeEntity,
          status: 'failed',
          updatedAt: new Date().toISOString(),
        }))
      );
    }
  }
}
